import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, Eye, EyeOff, Layers, Image, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function AdminCollections() {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCol, setEditingCol] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [colRes, venuesRes] = await Promise.all([
        axios.get(`${API}/admin/collections`),
        axios.get(`${API}/venues?limit=100`),
      ]);
      setCollections(colRes.data);
      setAllVenues(venuesRes.data || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (colId) => {
    if (!window.confirm("Delete this collection?")) return;
    try {
      await axios.delete(`${API}/admin/collections/${colId}`);
      setCollections(collections.filter((c) => c.collection_id !== colId));
      toast.success("Collection deleted");
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleTogglePublish = async (col) => {
    try {
      const res = await axios.put(`${API}/admin/collections/${col.collection_id}`, { is_published: !col.is_published });
      setCollections(collections.map((c) => c.collection_id === col.collection_id ? res.data : c));
      toast.success(col.is_published ? "Unpublished" : "Published");
    } catch (error) { toast.error("Failed to update"); }
  };

  return (
    <div data-testid="admin-collections-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Star className="h-6 w-6 text-primary" />{t("admin_collections:title")}</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="collection-create-btn"><Plus className="h-4 w-4 mr-2" /> New Collection</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Collection</DialogTitle></DialogHeader>
            <CollectionForm venues={allVenues} onSuccess={(c) => { setCollections([...collections, c]); setShowCreate(false); }} />
          </DialogContent>
        </Dialog>
      </div>

      {editingCol && (
        <Dialog open={!!editingCol} onOpenChange={() => setEditingCol(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Collection</DialogTitle></DialogHeader>
            <CollectionForm collection={editingCol} venues={allVenues} onSuccess={(c) => { setCollections(collections.map((x) => x.collection_id === c.collection_id ? c : x)); setEditingCol(null); }} />
          </DialogContent>
        </Dialog>
      )}

      {loading ? <div className="animate-pulse bg-muted h-64 rounded-xl" /> : collections.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((col) => (
            <div key={col.collection_id} className="bg-card rounded-xl border border-border overflow-hidden" data-testid={`collection-card-${col.collection_id}`}>
              <div className="h-32 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                {col.hero_image ? (
                  <img src={col.hero_image} alt={col.name} className="w-full h-full object-cover" />
                ) : (
                  <Layers className="h-10 w-10 text-primary/30" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-foreground">{col.name}</h3>
                  {col.is_published ? (
                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">Live</span>
                  ) : (
                    <span className="text-[10px] font-medium text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200">Draft</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{col.description}</p>
                <p className="text-xs text-muted-foreground mb-3">{(col.venue_ids || []).length} venues</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingCol(col)}><Edit className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(col)}>
                    {col.is_published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(col.collection_id)}>
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Layers className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("admin_collections:add_collection")}</h3>
          <p className="text-muted-foreground mb-4">Create themed venue collections like "Best Rooftop Bars" or "Family-Friendly".</p>
          <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" /> Create First Collection</Button>
        </div>
      )}
    </div>
  );
}

function CollectionForm({ collection, venues, onSuccess }) {
  const isEdit = !!collection;
  const [form, setForm] = useState({
    name: collection?.name || "", slug: collection?.slug || "", description: collection?.description || "",
    hero_image: collection?.hero_image || "", venue_ids: collection?.venue_ids || [],
    is_published: collection?.is_published || false, sort_order: collection?.sort_order || 0,
  });
  const [loading, setLoading] = useState(false);

  const toggleVenue = (venueId) => {
    setForm({
      ...form,
      venue_ids: form.venue_ids.includes(venueId)
        ? form.venue_ids.filter((id) => id !== venueId)
        : [...form.venue_ids, venueId],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, hero_image: form.hero_image || null };
      const res = isEdit
        ? await axios.put(`${API}/admin/collections/${collection.collection_id}`, payload)
        : await axios.post(`${API}/admin/collections`, payload);
      toast.success(isEdit ? "Collection updated" : "Collection created");
      onSuccess(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, ...(isEdit ? {} : { slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") }) })} required className="mt-1" data-testid="collection-name-input" /></div>
        <div><Label>Slug *</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required className="mt-1" /></div>
      </div>
      <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
      <div><Label>Hero Image URL</Label><Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className="mt-1" placeholder="https://..." /></div>
      <div>
        <Label>Select Venues ({form.venue_ids.length} selected)</Label>
        <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
          {venues.map((v) => (
            <label key={v.venue_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${form.venue_ids.includes(v.venue_id) ? "bg-primary/10" : "hover:bg-muted/40"}`}>
              <input type="checkbox" checked={form.venue_ids.includes(v.venue_id)} onChange={() => toggleVenue(v.venue_id)} className="rounded" />
              <span className="text-sm">{v.name}</span>
              {v.city_id && <span className="text-xs text-muted-foreground ml-auto">{v.city_id}</span>}
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Status</Label>
          <Select value={form.is_published ? "published" : "draft"} onValueChange={(v) => setForm({ ...form, is_published: v === "published" })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="mt-1" /></div>
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="collection-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} {isEdit ? "Update" : "Create"} Collection
      </Button>
    </form>
  );
}
